import styles from './UpgradesChangeNameDialog.module.scss';
import { useEffect, useState } from 'react';
import { Dialog } from 'subframe/components/Dialog';
import { IconButton } from 'subframe/components/IconButton';
import { TextField } from 'subframe/components/TextField';
import { Button } from 'subframe/components/Button';
import * as Yup from 'yup';
import * as Sentry from '@sentry/browser';
import {
  charLimitExceededErrorMessage,
  charLimits,
  fieldIsRequiredErrorMessage,
  invalidNameInputErrorMessage,
  validNameRegex,
} from 'constants/input-validation';
import { useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import FormProvider from 'components/FormProvider';
import {
  getUpgradeModeHumanReadable,
  UpgradeViewMode,
} from './UpgradesHelpers';

export function ChangeNameDialog({
  name,
  onConfirm,
  mode,
  open,
  setOpen,
}: {
  name: string;
  onConfirm: (newName: string) => void;
  mode: UpgradeViewMode;
  open: boolean;
  setOpen: (open: boolean) => void;
}): JSX.Element {
  const ChangeNameSchema = Yup.object().shape({
    name: Yup.string()
      .trim()
      .required(
        fieldIsRequiredErrorMessage(
          `${getUpgradeModeHumanReadable(mode)} Name`,
        ),
      )
      .max(
        charLimits.Name,
        charLimitExceededErrorMessage(
          `${getUpgradeModeHumanReadable(mode)} Name`,
          charLimits.Name,
        ),
      )
      .matches(validNameRegex, invalidNameInputErrorMessage),
  });
  const defaultValues = {
    name,
  };

  const methods = useForm({
    resolver: yupResolver(ChangeNameSchema),
    defaultValues: defaultValues,
  });

  const {
    handleSubmit,
    formState: { isSubmitting, errors },
    getValues,
  } = methods;

  useEffect(() => {
    if (!open) {
      methods.reset(defaultValues);
    }
  }, [open]);

  useEffect(() => {
    methods.reset(defaultValues);
  }, [name]);

  const handleChangeName = async () => {
    try {
      if (getValues('name') && getValues('name').localeCompare(name) !== 0) {
        onConfirm(getValues('name'));
      }
      handleOpenChange();
    } catch (error) {
      Sentry.captureException(error);
    }
  };

  const handleOpenChange = (open = false) => {
    setOpen(open);
  };

  return (
    <Dialog
      open={open}
      onOpenChange={(openStatus) => handleOpenChange(openStatus)}
    >
      <Dialog.Content className="h-auto w-80 flex-none">
        <FormProvider
          methods={methods}
          onSubmit={handleSubmit(handleChangeName)}
          style={{
            display: 'contents',
          }}
        >
          <div className={styles['stack-3244c09e']}>
            <div className={styles['stack']}>
              <span className={styles['subheaderText']}>
                Edit {getUpgradeModeHumanReadable(mode)}
              </span>
              <IconButton
                icon="FeatherX"
                size="medium"
                onClick={() => handleOpenChange()}
              />
            </div>
            <div className={styles['stack-921cf26e']}>
              <TextField
                helpText={errors.name?.message}
                error={methods.getFieldState('name').invalid}
                label="Name"
                className="flex-[0_0_auto] h-auto w-full"
              >
                <TextField.Input
                  {...methods.register('name')}
                  onKeyDown={(event) => {
                    if (event.key === 'Enter') {
                      handleSubmit(handleChangeName)();
                    }
                  }}
                />
              </TextField>
            </div>
            <div className={styles['stack-24355dd3']}>
              <Button
                size="large"
                variant="brand-primary"
                icon="FeatherSave"
                loading={isSubmitting}
                onClick={handleSubmit(handleChangeName)}
                disabled={isSubmitting}
              >
                Save
              </Button>
            </div>
          </div>
        </FormProvider>
      </Dialog.Content>
    </Dialog>
  );
}
